import {
  gql,
  QueryFunctionOptions,
  useMutation,
  useQuery,
} from "@apollo/client";
import { useOutletContext } from "react-router";
import { IOutletAuth, IProfile } from "types";
import { USER_PROFILE_FIELDS } from "./UserProfile.contants";
import { useCheckPremium } from "utils";

const GET_USER_PROFILE = gql`
  query getUser($email: String!, $isAuth: Boolean!) {
    users(where: { email: $email }) {
      ${USER_PROFILE_FIELDS}
    }
  }
`;

export function useUserProfile() {
  const context = useOutletContext<IOutletAuth>();
  const isPremium = useCheckPremium();
  const {
    loading,
    error,
    data: { users: [user] = [] } = {},
  } = useQuery<{
    users: IProfile[];
  }>(GET_USER_PROFILE, {
    variables: { email: context.user?.attributes?.email, isAuth: isPremium },
    context: { api: "userProfile" },
    skip: !context.isAuth,
  });

  return { loading, error, data: { user } };
}

export const UPDATE_USER_PROFILE = gql`
  mutation updateUsers($email: String!, $isAuth: Boolean!, $update: UserUpdateInput!) {
    updateUsers(where: { email: $email }, update: $update) {
      users {${USER_PROFILE_FIELDS}}
    }
  }
`;

export function useUpdateUserProfile() {
  const isPremium = useCheckPremium();
  return useMutation<{
    id: string;
    update: Omit<IProfile, "organization" | "createdAt">;
  }>(UPDATE_USER_PROFILE, {
    variables: { isAuth: isPremium },
    context: { api: "userProfile" },
  });
}

export function useUsersOptionsQuery({
  name,
  ...rest
}: {
  name: string | undefined;
} & QueryFunctionOptions<
  {
    users: Partial<IProfile>[];
  },
  { phrase: string | undefined }
>) {
  const { loading, error, data } = useQuery<
    {
      users: Partial<IProfile>[];
    },
    { phrase: string | undefined }
  >(
    gql`
      query getUsers($phrase: String!) {
        users(
          options: { limit: 20 }
          fulltext: { UserFullText: { phrase: $phrase } }
        ) {
          id
          firstName
          lastName
          profilePic
        }
      }
    `,
    {
      variables: {
        phrase: name
          ? "+" + name.replaceAll("-", " ").replaceAll(" ", " +") + "*"
          : "*",
      },
      context: { api: "userProfile" },
      ...rest,
    }
  );
  return { loading, error, data };
}
